import React, { memo } from 'react';

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';

import Styles from './ProceduralStepsEMAReview.styles';
import type {
  ProceduralStepDraft,
  ProceduralStepDraftField,
  ProceduralStepsReviewModel
} from './ProceduralStepsEMAReview.types';

type FieldConfig = {
  field: ProceduralStepDraftField;
  label: string;
  placeholder: string;
};

const FIELDS: FieldConfig[] = [
  {
    field: 'applNo',
    label: 'Application Number',
    placeholder: 'Enter Application Number'
  },
  { field: 'scope', label: 'Scope', placeholder: 'Enter Scope' },
  {
    field: 'opinion',
    label: 'Opinion/Notification',
    placeholder: 'Enter Opinion/Notification'
  },
  {
    field: 'commissionDecision',
    label: 'Commission Decision',
    placeholder: 'Enter Commission Decision'
  },
  {
    field: 'productInfoAffected',
    label: 'Product Information Affected',
    placeholder: 'Enter Product Information Affected'
  },
  { field: 'summary', label: 'Summary', placeholder: 'Enter Summary' }
];

type ReviewHeaderProps = {
  loading: boolean;
  hasCurationId: boolean;
  hasSelectedStep: boolean;
  total: number;
  sessionNewCount: number;
  onBack: () => void;
  onEdit: () => void;
  onAdd: () => void;
  onDone: () => void;
};

const ReviewHeaderView = ({
  loading,
  hasCurationId,
  hasSelectedStep,
  total,
  sessionNewCount,
  onBack,
  onEdit,
  onAdd,
  onDone
}: ReviewHeaderProps) => {
  const classes = Styles();
  const mutationsDisabled = !hasCurationId || loading;

  return (
    <Box className={classes.toolbar}>
      <Box className={classes.toolbarActions}>
        <Button className={classes.button} onClick={onBack} variant='contained'>
          Go back
        </Button>
        <Button
          className={classes.button}
          onClick={onEdit}
          disabled={mutationsDisabled || !hasSelectedStep}
          variant='contained'>
          Edit
        </Button>
        <Button
          className={classes.button}
          onClick={onAdd}
          disabled={mutationsDisabled}
          variant='contained'>
          Add
        </Button>
        <Button
          className={classes.button}
          onClick={onDone}
          disabled={mutationsDisabled}
          variant='contained'>
          Done
        </Button>
      </Box>
      <Box>
        <Typography className={classes.heading2}>
          Number of Applications in database: {total}
        </Typography>
        <Typography className={classes.heading2}>
          Number of New Applications: {sessionNewCount}
        </Typography>
        <Typography className={classes.heading2}>Total Applications: {total}</Typography>
      </Box>
    </Box>
  );
};

export const ProceduralStepsReviewHeader = memo(ReviewHeaderView);

/* eslint-disable no-unused-vars -- callback parameter names document the typed props */
type DraftFieldProps = FieldConfig & {
  value: string;
  onChange(field: ProceduralStepDraftField, value: string): void;
};
/* eslint-enable no-unused-vars */

const DraftFieldView = ({ field, label, placeholder, value, onChange }: DraftFieldProps) => {
  const classes = Styles();
  const inputId = `procedural-step-${field}`;

  return (
    <>
      <Typography component='label' htmlFor={inputId} className={classes.label}>
        {label}:
      </Typography>
      <TextareaAutosize
        id={inputId}
        className={classes.textAreaField}
        minRows={2}
        value={value}
        onChange={event => onChange(field, event.target.value)}
        placeholder={placeholder}
      />
    </>
  );
};

const DraftField = memo(DraftFieldView);

/* eslint-disable no-unused-vars -- callback parameter names document the typed props */
type StepFormProps = {
  draft: ProceduralStepDraft;
  loading: boolean;
  onFieldChange(field: ProceduralStepDraftField, value: string): void;
};
/* eslint-enable no-unused-vars */

const StepForm = ({ draft, loading, onFieldChange }: StepFormProps) => {
  const classes = Styles();

  return (
    <Box className={classes.form}>
      {FIELDS.map(config =>
        loading ? (
          <Skeleton key={config.field} variant='rect' width='100%' height={40} />
        ) : (
          <DraftField
            key={config.field}
            field={config.field}
            label={config.label}
            placeholder={config.placeholder}
            value={draft[config.field]}
            onChange={onFieldChange}
          />
        )
      )}
    </Box>
  );
};

type StepDetailsProps = {
  draft: ProceduralStepDraft;
};

const StepDetails = ({ draft }: StepDetailsProps) => {
  const classes = Styles();

  return (
    <Box className={classes.details}>
      {FIELDS.map(({ field, label }) => (
        <Typography component='div' key={field} className={classes.label}>
          {label}:
          <Typography component='div' className={classes.detailValue}>
            {draft[field]}
          </Typography>
        </Typography>
      ))}
      <Typography component='div' className={classes.label}>
        Status:
        <Typography component='div' className={classes.detailValue}>
          {draft.status}
        </Typography>
      </Typography>
    </Box>
  );
};

type ConfirmationDialogProps = {
  open: boolean;
  title: string;
  description: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const ConfirmationDialog = ({
  open,
  title,
  description,
  loading,
  onClose,
  onConfirm
}: ConfirmationDialogProps) => {
  const classes = Styles();

  return (
    <Dialog open={open} onClose={onClose}>
      <Box className={classes.dialog}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{description}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading} className={classes.confirm}>
            No
          </Button>
          <Button onClick={onConfirm} disabled={loading} className={classes.confirm} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

type SearchProps = Pick<ProceduralStepsReviewModel, 'filteredSteps' | 'state' | 'actions'>;

const StepSearch = ({ filteredSteps, state, actions }: SearchProps) => {
  const classes = Styles();

  return (
    <>
      <Box className={classes.searchRow}>
        <TextareaAutosize
          className={classes.textAreaField}
          minRows={2}
          onClick={() => actions.setDropdownOpen(true)}
          value={state.query}
          onChange={event => actions.setQuery(event.target.value)}
          placeholder='Enter Application Number'
          autoComplete='off'
        />
        <Button className={classes.button} onClick={actions.search} variant='contained'>
          Search
        </Button>
      </Box>
      <Box className={state.dropdownOpen ? classes.showDropdown : classes.hideDropdown}>
        <ul className={classes.dropdownList}>
          {filteredSteps.map(step => (
            <MenuItem
              key={step.id}
              className={classes.dropdownItem}
              onClick={() => actions.chooseQuery(step.appln_no || '')}
              value={step.appln_no || ''}>
              <Typography>{step.appln_no}</Typography>
              <Typography>{step.status || 'incomplete'}</Typography>
            </MenuItem>
          ))}
        </ul>
      </Box>
    </>
  );
};

type ReviewPanelProps = {
  model: ProceduralStepsReviewModel;
};

export const ProceduralStepsReviewPanel = ({ model }: ReviewPanelProps) => {
  const classes = Styles();
  const { state, selectedStep, filteredSteps, actions, productNumber } = model;
  const editing = state.mode !== 'view';

  return (
    <Box className={classes.reviewBox}>
      <Box padding={1}>
        <Typography className={classes.heading}>
          Select an Application for {productNumber}.
        </Typography>
      </Box>

      {state.loading && state.mode === 'view' ? (
        <Box padding={1}>
          <Skeleton variant='rect' width='100%' height={50} />
        </Box>
      ) : null}

      {!state.loading && state.mode === 'view' ? (
        <StepSearch filteredSteps={filteredSteps} state={state} actions={actions} />
      ) : null}

      {editing ? (
        <StepForm draft={state.draft} loading={state.loading} onFieldChange={actions.updateDraft} />
      ) : null}

      {selectedStep && state.mode === 'view' ? <StepDetails draft={state.draft} /> : null}

      {editing ? (
        <Box className={classes.formActions}>
          <Button
            className={classes.button}
            onClick={actions.cancelChanges}
            disabled={state.loading}
            variant='contained'>
            Cancel
          </Button>
          <Button
            className={classes.button}
            onClick={actions.requestSubmit}
            disabled={state.loading}
            variant='contained'>
            Submit
          </Button>
        </Box>
      ) : null}

      <ConfirmationDialog
        open={state.dialogs.submit}
        title='Confirm Submission'
        description='Are you sure you want to submit the changes?'
        loading={state.loading}
        onClose={actions.closeSubmitDialog}
        onConfirm={actions.confirmSubmit}
      />

      <ConfirmationDialog
        open={state.dialogs.duplicate}
        title='Alert'
        description={`An application with the Product Number: ${productNumber} and Application Number: ${state.draft.applNo} already exists. Do you want to continue?`}
        loading={state.loading}
        onClose={actions.closeDuplicateDialog}
        onConfirm={actions.confirmDuplicate}
      />
    </Box>
  );
};

type PdfViewerProps = {
  pdfUrl: string;
};

const PdfViewerView = ({ pdfUrl }: PdfViewerProps) => {
  const classes = Styles();

  return pdfUrl ? (
    <Box className={classes.pdfContainer}>
      <iframe
        title='Procedural steps PDF'
        src={pdfUrl}
        className={`${classes.pdfContainer} ${classes.iframe}`}
      />
    </Box>
  ) : (
    <Box className={`${classes.pdfContainer} ${classes.noPdf}`}>
      <Typography className={classes.notAvailable}>No pdf is available</Typography>
    </Box>
  );
};

export const ProceduralStepsPdfViewer = memo(PdfViewerView);

type DoneDialogProps = {
  model: ProceduralStepsReviewModel;
};

export const ProceduralStepsDoneDialog = ({ model }: DoneDialogProps) => (
  <ConfirmationDialog
    open={model.state.dialogs.done}
    title='Complete Procedural Steps'
    description='Are you sure you want to mark the Procedural Steps branch as Done?'
    loading={model.state.loading}
    onClose={model.actions.closeDoneDialog}
    onConfirm={model.actions.confirmDone}
  />
);
